import { createTimeline, stagger } from '../../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, resolveDuration, resolveStagger, BLUR } from '../motion-tokens.js';

export const statsV1 = (el) => {
    const items = el.querySelectorAll('.stat-item, .stat-card');
    const glows = el.querySelectorAll('.stat-glow, .stat-highlight, [class*="glow"]');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    if (items.length) tl.add(items, {
        opacity:    [0, 1],
        translateY: [20, 0],
        filter:     ['blur(3px)', 'blur(0px)'],
        delay:      stagger(resolveStagger(STAGGER.normal)),
        duration:   resolveDuration(DURATION.slow),
    });

    const numbers = el.querySelectorAll('.stat-number, .stat-value, .stat-item h3');
    if (numbers.length) {
        tl.add(numbers, {
            opacity:    [0, 1],
            translateY: [-40, 0],
            scaleY:     [1.3, 1],
            transformOrigin: '50% 0%',
            delay:      stagger(resolveStagger(STAGGER.normal), { from: 'last', start: 250 }),
            duration:   resolveDuration(DURATION.fast),
            ease:       SPRING_PRESETS.magnetic,
        }, '-=400');
    }

    if (glows.length) {
        tl.add(glows, {
            opacity: [0.3, 0.8, 0.3],
            duration: 1200,
            ease:    'inOutSine',
        }, '-=400');
    }

    return tl;
};

export const statsV2 = (el) => {
    const cards = el.querySelectorAll('.stat-item, .stat-card');
    if (!cards.length) return null;

    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    tl.add(cards, {
        opacity:    [0, 1],
        translateY: [20, 0],
        filter:     [BLUR.normal, BLUR.none],
        delay:      stagger(resolveStagger(STAGGER.fast)),
        duration:   resolveDuration(DURATION.slow),
    });

    const numbers = el.querySelectorAll('.stat-number, .stat-value, .stat-item h3');
    if (numbers.length) {
        tl.add(numbers, {
            opacity:    [0, 1],
            translateY: [-40, 0],
            scaleY:     [1.3, 1],
            transformOrigin: '50% 0%',
            delay:      stagger(resolveStagger(STAGGER.normal), { from: 'last', start: 250 }),
            duration:   resolveDuration(DURATION.fast),
            ease:       SPRING_PRESETS.magnetic,
        }, '-=400');
    }

    tl.add(cards, {
        backgroundSize:     ['100% 0%', '100% 100%'],
        backgroundPosition: ['0 100%', '0 100%'],
        delay:              stagger(resolveStagger(STAGGER.fast)),
        duration:           resolveDuration(DURATION.slow * 1.2),
        ease:               'outCubic',
    }, '-=600');

    return tl;
};

export const statsV3 = (el) => {
    const separators = el.querySelectorAll('.stat-separator, .stat-divider, [class*="separator"], [class*="divider"]');
    const items      = el.querySelectorAll('.stat-item, .stat-number, .stat-value');

    separators.forEach(s => { s.style.opacity = '0'; s.style.transform = 'scaleY(0)'; s.style.transformOrigin = 'top center'; });
    items.forEach(item => { item.style.opacity = '0'; item.style.transform = 'translateY(15px)'; });

    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    if (separators.length) {
        tl.add(separators, {
            scaleY:          [0, 1],
            opacity:         [0, 1],
            duration:        resolveDuration(DURATION.slow),
            ease:            'linear',
            transformOrigin: 'top center',
        });
    }

    if (items.length) {
        tl.add(items, {
            opacity:    [0, 1],
            translateY: [15, 0],
            delay:      stagger(resolveStagger(STAGGER.fast)),
            duration:   resolveDuration(DURATION.normal),
        }, separators.length ? '-=400' : 0);
    }

    const numbers = el.querySelectorAll('.stat-number, .stat-value, .stat-item h3');
    if (numbers.length) {
        tl.add(numbers, {
            opacity:    [0, 1],
            translateY: [-40, 0],
            scaleY:     [1.3, 1],
            transformOrigin: '50% 0%',
            delay:      stagger(resolveStagger(STAGGER.normal), { from: 'last', start: 250 }),
            duration:   resolveDuration(DURATION.fast),
            ease:       SPRING_PRESETS.magnetic,
        }, '-=400');
    }

    return tl;
};

/**
 * v4 "Tablero KPI": el CSS re-maqueta el DOM por defecto del render (.stats-divider) como
 * REJILLA de tiles con pestaña de acento e índice editorial — ya no es la fila de v1/v3, así
 * que la coreografía es propia y no un alias: las piezas entran como un tablero (cascada
 * diagonal sobre los offsets REALES, igual que galleryV1/bento), luego hace pop el badge del
 * icono y por último sube la cifra (la firma común de todas las variantes de stats).
 */
export const statsV4 = (el) => {
    const tiles = [...el.querySelectorAll('.stat-item, .stat-card')];
    if (!tiles.length) return null;

    const badges  = el.querySelectorAll('.stat-item .block-icon-svg, .stat-card .block-icon-svg');
    const numbers = el.querySelectorAll('.stat-item [data-field="name"], .stat-card [data-field="name"]');

    // Estado inicial forzado (previene el flash antes del primer frame).
    tiles.forEach(t => {
        t.style.opacity = '0';
        t.style.transform = 'translateY(24px) scale(0.96)';
        t.style.filter = BLUR.normal;
    });
    badges.forEach(b => { b.style.opacity = '0'; b.style.transform = 'scale(0.7)'; });

    // Retícula real: el auto-fit del CSS da un nº de columnas distinto por breakpoint.
    const columnas = new Set();
    const filas = new Set();
    tiles.forEach(t => { columnas.add(t.offsetLeft); filas.add(t.offsetTop); });

    const tl = createTimeline({ defaults: { ease: EASE.refined } });

    tl.add(tiles, {
        opacity:    [0, 1],
        translateY: [24, 0],
        scale:      [0.96, 1],
        filter:     [BLUR.normal, BLUR.none],
        delay:      stagger(resolveStagger(80), {
            grid: [columnas.size || 1, filas.size || 1], from: 'first',
        }),
        duration:   resolveDuration(DURATION.slow),
        ease:       SPRING_PRESETS.deluxe,
    });

    if (badges.length) tl.add(badges, {
        opacity:  [0, 1],
        scale:    [0.7, 1],
        delay:    stagger(resolveStagger(STAGGER.fast)),
        duration: resolveDuration(DURATION.fast),
        ease:     SPRING_PRESETS.magnetic,
    }, '-=800');

    if (numbers.length) tl.add(numbers, {
        opacity:    [0, 1],
        translateY: [-28, 0],
        scaleY:     [1.25, 1],
        transformOrigin: '50% 0%',
        delay:      stagger(resolveStagger(STAGGER.normal), { from: 'last', start: 150 }),
        duration:   resolveDuration(DURATION.fast),
        ease:       SPRING_PRESETS.magnetic,
    }, '-=500');

    return tl;
};
